// Wave 20.49g — Aero AI contextual nudge that renders above the Wake.
//
// Composes:
//   - browser geolocation → weather.aero.chat/thrucast for "now"
//   - usePositionsAnalytics for the rider's real positions (aliases)
//   - comfort.ts helpers for weather phrasing
//
// Returns a single-sentence headline, an optional weather subline,
// and a curated list of 3–4 action buttons the rider can tap. No LLM
// call in this file — that's a follow-up layer wrapped around the
// same output. Deterministic + resilient to geolocation denial and
// weather failures (falls back to a positions-only nudge).

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  usePositionsAnalytics,
  type PositionStat,
} from './usePositionsAnalytics';
import {
  computeComfort,
  type ComfortInput,
} from '../services/comfort';
import { useUnitSystem, type UnitSystem } from '../services/useUnitSystem';

// Wave 20.49dh — Weather goes through the api proxy.
const API_BASE = process.env.REACT_APP_API || 'https://api.aero.chat';
const WEATHER_API = `${API_BASE}/weather`;
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

export interface WakeNudgeAction {
  key: string;
  label: string;
  href: string;
}

export interface WakeNudge {
  loading: boolean;
  headline: string;
  subline: string | null;
  focus: PositionStat | null;
  weather: ComfortInput | null;
  actions: WakeNudgeAction[];
  refresh: () => void;
}

const PRETTY_CLASS: Record<string, string> = {
  aero_tuck: 'aero tuck',
  drops: 'drops',
  hoods: 'hoods',
  tt: 'TT',
  tops: 'tops',
  standing: 'standing',
  upright: 'tops', // legacy alias — pre-Wave-20.49cj sessions
  seated: 'seated',
};
function prettyClass(cls: string): string {
  return PRETTY_CLASS[cls] ?? cls.replace(/_/g, ' ');
}
function labelForPosition(p: PositionStat): string {
  return p.alias?.trim() ? p.alias.trim() : prettyClass(p.class);
}

// Pick the position with the widest CdA spread — that's the one
// with the most upside left to dial in. Fallback to the position
// held longest so single-position riders still get a focus.
function pickFocus(positions: PositionStat[]): PositionStat | null {
  if (!positions.length) return null;
  const withSpread = positions
    .map((p) => ({ p, spread: p.worstCda - p.meanCda }))
    .filter((x) => Number.isFinite(x.spread));
  if (withSpread.length) {
    withSpread.sort((a, b) => b.spread - a.spread);
    if (withSpread[0].spread > 0.005) return withSpread[0].p;
  }
  const byHold = [...positions].sort((a, b) => b.totalHoldSec - a.totalHoldSec);
  return byHold[0] ?? null;
}

async function fetchNowWeather(
  lat: number,
  lng: number,
): Promise<ComfortInput | null> {
  const token = (() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  })();
  if (!token) return null;
  const now = new Date().toISOString();
  const url = `${WEATHER_API}/thrucast?lat=${lat.toFixed(4)}&lng=${lng.toFixed(4)}&t=${encodeURIComponent(now)}`;
  try {
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const body = await res.json();
    return {
      temperature_c: body?.temperature_c ?? null,
      wind_speed_ms: body?.wind_speed_ms ?? null,
      humidity_pct: body?.humidity_pct ?? null,
      precip_mm_1h: body?.precip_mm_1h ?? null,
    };
  } catch {
    return null;
  }
}

function getLocation(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    const timer = window.setTimeout(() => resolve(null), 8000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(timer);
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => {
        window.clearTimeout(timer);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  });
}

// Wave 20.49j — on the Wake screen the AI sentence sits alone with
// no surrounding weather panel. That means comfortHeadline's
// designed-for-panel phrases ("Warm — carry extra water") land as
// context-free instructions. Build a self-contained sentence
// instead: state the actual temperature + wind (in rider units)
// so the reader always knows what we're talking about.

function fmtTempPart(c: number | null | undefined, u: UnitSystem): string | null {
  if (c == null || !Number.isFinite(c)) return null;
  if (u === 'imperial') return `${Math.round((c * 9) / 5 + 32)}°F`;
  return `${Math.round(c)}°C`;
}

function fmtWindPart(mps: number | null | undefined, u: UnitSystem): string {
  if (mps == null || !Number.isFinite(mps) || mps < 1.5) return 'calm winds';
  const v =
    u === 'imperial'
      ? `${(mps * 2.23694).toFixed(0)} mph`
      : `${(mps * 3.6).toFixed(0)} km/h`;
  if (mps < 4) return `light ${v} winds`;
  if (mps < 8) return `${v} winds`;
  if (mps < 12) return `gusty ${v} winds`;
  return `strong ${v} winds`;
}

function weatherPhrase(
  weather: ComfortInput,
  units: UnitSystem,
): { conditions: string; verdict: 'ride' | 'indoor' } {
  const temp = fmtTempPart(weather.temperature_c, units);
  const wind = fmtWindPart(weather.wind_speed_ms, units);
  const rain = (weather.precip_mm_1h ?? 0) > 0.5;
  const conditions = temp ? `${temp} with ${wind}` : `${wind}`;

  // Verdict — should the rider go ride, or refine indoors?
  const c = computeComfort(weather);
  const tooHot = (weather.temperature_c ?? 18) > 30;
  const tooCold = (weather.temperature_c ?? 18) < 0;
  const badScore = c.score < 4.5;
  if (rain || tooHot || tooCold || badScore) {
    return { conditions, verdict: 'indoor' };
  }
  return { conditions, verdict: 'ride' };
}

function buildHeadline(
  focus: PositionStat | null,
  weather: ComfortInput | null,
  units: UnitSystem,
): { headline: string; subline: string | null } {
  if (!focus && !weather) {
    return {
      headline: 'Record your first aero session to start building your wake.',
      subline: null,
    };
  }
  if (weather) {
    const { conditions, verdict } = weatherPhrase(weather, units);
    if (focus) {
      const name = labelForPosition(focus);
      if (verdict === 'ride') {
        return {
          headline: `It's ${conditions} outside — a great window to test your ${name} position on a ride.`,
          subline: null,
        };
      }
      return {
        headline: `It's ${conditions} outside — a good time to refine your ${name} position with an aero session indoors.`,
        subline: null,
      };
    }
    return {
      headline:
        verdict === 'ride'
          ? `It's ${conditions} outside — good conditions to head out on a ride.`
          : `It's ${conditions} outside — better window for an aero session indoors.`,
      subline: null,
    };
  }
  // focus only, no weather (location denied or weather fetch failed)
  return {
    headline: `You've held your ${labelForPosition(focus!)} position longest — worth another rep.`,
    subline: null,
  };
}

function buildActions(focus: PositionStat | null): WakeNudgeAction[] {
  const out: WakeNudgeAction[] = [];
  out.push({
    key: 'start',
    label: focus
      ? `Start an aero session · ${labelForPosition(focus)}`
      : 'Start an aero session',
    href: focus
      ? `/session/focus?preselect=${encodeURIComponent(focus.class)}`
      : '/session/focus',
  });
  if (focus) {
    out.push({
      key: 'drill',
      label: `Drill into ${labelForPosition(focus)}`,
      href: `/positions/${encodeURIComponent(focus.class)}`,
    });
  }
  out.push({ key: 'feed', label: 'Go to your feed', href: '/feed' });
  out.push({ key: 'forecast', label: 'See the forecast', href: '/feed#weather' });
  return out;
}

export function useWakeNudge(): WakeNudge {
  const analytics = usePositionsAnalytics();
  const units = useUnitSystem();
  const [weather, setWeather] = useState<ComfortInput | null>(null);
  const [wxLoading, setWxLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setWxLoading(true);
    (async () => {
      const loc = await getLocation();
      if (!loc) {
        if (!cancelled) {
          setWeather(null);
          setWxLoading(false);
        }
        return;
      }
      const w = await fetchNowWeather(loc.lat, loc.lng);
      if (!cancelled) {
        setWeather(w);
        setWxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const focus = useMemo(
    () => pickFocus(analytics.data?.positions ?? []),
    [analytics.data?.positions],
  );

  const { headline, subline } = useMemo(
    () => buildHeadline(focus, weather, units),
    [focus, weather, units],
  );
  const actions = useMemo(() => buildActions(focus), [focus]);

  const refresh = useCallback(() => {
    setNonce((n) => n + 1);
    analytics.reload();
  }, [analytics]);

  return {
    loading: analytics.loading || wxLoading,
    headline,
    subline,
    focus,
    weather,
    actions,
    refresh,
  };
}
